import React, { FC, useCallback, useEffect, useState } from 'react'

import { ContactFormWidget } from '../ContactFormWidget/ContactFormWidget'
import { Dictionary } from '../../utils/types'

import { AiChatBotWidget } from '../AiChatBotWidget/AiChatBotWidget'

import { NavigationLink } from './NavMenu'
import { PageBannerProps } from './PageBanner'
import { HeaderManager } from './HeaderManager'

export type Banner = {
  id: string
  title: string
  description: string
  link?: Link
  textColor: string
  image?: UmbracoImage
  backgroundColor?: string
  icon?: string
}

export type SearchFilter = {
  label: string
  identifier: string
  placeholder?: string
  countries?: CountryFilter[]
}

export type CountryFilter = {
  label: string
  countryCode: string
}

export type Link = {
  url: string
  text: string
  target?: string
}

export type UmbracoImage = {
  url: string
  alt?: string
}

export type ContactUsDetails = {
  name?: string
  email?: string
  phone?: string
  reason?: string
  message?: string
  url?: string
}

export type HeaderWidgetManagerProps = {
  domainCode: string
  countryFilterEnabled: boolean
  defaultCountryFilter?: {
    label: string
    countryCode: string
  }
  otherCountriesFilter?: {
    label: string
    countryCode: string
  }[]
  externalSearchLink?: { text: string; url: string }
  searchWidgetDisplayMode?: 'WithFilters' | 'WithoutFilters'
  cmsFaqUrl: string | undefined
  anonymousSubscriberId: string | undefined
  loggedIn: boolean
  userId: string | undefined
  userName: string
  fullName: string
  subscriberExternalId: string | undefined
  paymentOptionCode: 'PPR' | string | undefined
  isAuthorizedForOrdering: boolean
  logoUrl: {
    url: string
    alt?: string
  }
  countOfOrderItems: number
  loginUrl: string
  showBulkImportMenuInHeader: boolean | undefined
  showBulkImportLinkAboveSearchButton: boolean | undefined
  showAsBulkImportButton: boolean | undefined
  showMulticolumnDropdown: boolean | undefined
  countUnreadNotifications: number
  selectedMenuItem: string | undefined
  logout: () => void
  loginAsLogout: () => void
  activeButton: 'Archive' | 'Profile' | 'BulkImport' | undefined
  links: {
    Archive: React.FunctionComponent
    Profile: React.FunctionComponent
    Bulk: React.FunctionComponent
    Cart: React.FunctionComponent
    Notifications: React.FunctionComponent
    Home: React.FunctionComponent
    Slash: React.FunctionComponent
    AdditionalBulkHistory: React.FunctionComponent
  }
  handleMarkAllNotificationsReadClick: () => void
  queryString: string
  onSearchClick: (params: { query: string; filter: string; countryFilter?: string }) => void
  changeLanguage: (lng: string) => void
  hasBriqBulkImportRole: boolean
  ImportHistoryLink: React.FunctionComponent
  toggle: (name: string) => void
  close: (name: string) => void
  navigationLinks: NavigationLink[]
  cmsHomeUrl: string | undefined
  searchFilters: SearchFilter[]
  footerLinks: {
    url: string
    text: string
    target?: string
  }[]
  logoImg: {
    url: string
    alt?: string
  }
  contactWidget:
    | {
        id: string
        title: string
        description: string
        link?: {
          url: string
          text: string
          target?: string
        }
        textColor: string
        image?: {
          url: string
          alt?: string
        }
        backgroundColor?: string
        icon?: string
      }
    | undefined
  handleContactUsRequestSubmit?: (data: ContactUsDetails) => void
  contactRequestLoading?: boolean
  contactRequestError?: any
  contactRequestDone?: boolean
  showContactUsFormWidget: boolean | undefined
  contactUsReason?: Dictionary<string>
  resetContactUs?: () => void
  showAiChatbotButton: boolean
  sessionTimeout: number | undefined
} & PageBannerProps

export const HeaderWidgetManager: FC<HeaderWidgetManagerProps> = ({
  handleContactUsRequestSubmit,
  contactRequestLoading,
  contactRequestError,
  contactRequestDone,
  showContactUsFormWidget,
  contactUsReason,
  resetContactUs,
  domainCode,
  loggedIn,
  userId,
  subscriberExternalId,
  paymentOptionCode,
  isAuthorizedForOrdering,
  sessionTimeout,
  ...rest
}: HeaderWidgetManagerProps) => {
  const [aiChatbotWidgetState, setAiChatbotWidgetState] = useState<{
    toggle: boolean
    reportId?: string
    countryCode?: string
  }>({
    toggle: false
  })

  const { toggle: aiChatbotToggle, reportId, countryCode } = aiChatbotWidgetState

  const closeCallback = useCallback(() => {
    setAiChatbotWidgetState({ toggle: false })
  }, [setAiChatbotWidgetState])

  const chatbotMessageEventHandler = useCallback(
    (event?: Event) => {
      if ((event as any)?.origin !== window.location.origin) {
        return
      }

      if ((event as any)?.data?.name === 'openAiChatbot') {
        setAiChatbotWidgetState({
          toggle: true,
          reportId: (event as any)?.data?.reportId,
          countryCode: (event as any)?.data?.countryCode
        })
      }

      if ((event as any)?.data?.name === 'closeAiChatbot') {
        closeCallback()
      }
    },
    [setAiChatbotWidgetState, closeCallback]
  )

  useEffect(() => {
    window.addEventListener('message', chatbotMessageEventHandler)

    return () => window.removeEventListener('message', chatbotMessageEventHandler)
  }, [chatbotMessageEventHandler])

  return (
    <>
      <HeaderManager
        {...rest}
        loggedIn={loggedIn}
        toggleAiChatbotWidget={() => setAiChatbotWidgetState({ toggle: true })}
      />
      {showContactUsFormWidget && (
        <ContactFormWidget
          handleContactUsRequestSubmit={handleContactUsRequestSubmit}
          contactRequestLoading={contactRequestLoading}
          contactRequestError={contactRequestError}
          contactRequestDone={contactRequestDone}
          contactUsReason={contactUsReason}
          resetContactUs={resetContactUs}
        />
      )}
      {aiChatbotToggle && (
        <AiChatBotWidget
          close={closeCallback}
          domainCode={domainCode}
          userProfile={
            loggedIn ? { userId, subscriberExternalId, paymentOptionCode, isAuthorizedForOrdering } : undefined
          }
          reportId={reportId}
          countryCode={countryCode}
          refreshCookieIntervalMs={Math.round(((sessionTimeout ?? 0) / 4) * 3)}
        />
      )}
    </>
  )
}
